import { MxAccount } from "@modules/forme/types";
import {
  USE_ACCOUNTS_DATA_KEY,
  USE_ACCOUNTS_PATH,
} from "@modules/networth/constants";
import { isNumber } from "lodash";
import { type QueryClient } from "react-query";

interface MXUpdateEvent extends Record<string, any> {
  type: "updated" | "created" | "deleted";
  payload: {
    account_subtype: string;
    account_type: string;
    balance: number;
    guid: string;
    id: string;
    is_closed: boolean;
    is_hidden: boolean;
    is_manual: boolean;
    member_guid: string;
    metadata: null;
    name: string;
    property_type: string;
    type: string;
  };
}

const updateAccountBalance = async (
  event: MXUpdateEvent,
  queryClient: QueryClient
) => {
  const QUERY_KEY = [`forme/${USE_ACCOUNTS_PATH}/${USE_ACCOUNTS_DATA_KEY}`, {}];
  await queryClient.cancelQueries({ queryKey: [QUERY_KEY] });

  const previousAccounts: MxAccount[] | undefined =
    queryClient.getQueryData(QUERY_KEY);

  if (event.payload.type === "account") {
    if (event.type === "deleted") {
      if (!previousAccounts) {
        return;
      }

      const activeAccounts = previousAccounts.reduce((memo, account) => {
        if (account.GUID !== event.payload.guid) {
          memo.push(account);
        }
        return memo;
      }, [] as MxAccount[]);
      queryClient.setQueryData(QUERY_KEY, activeAccounts);
    }

    if (event.type === "updated") {
      if (!previousAccounts) {
        return;
      }

      const updatedAccounts = previousAccounts.map((account) => {
        if (account.GUID === event.payload.guid && event.payload.balance) {
          const key = isNumber(account.BALANCE)
            ? "BALANCE"
            : "AVAILABLE_BALANCE";
          return {
            ...account,
            [key]: event.payload.balance,
          };
        }
        return account;
      });
      queryClient.setQueryData(QUERY_KEY, updatedAccounts);
    }

    if (event.type === "created") {
      const newAccount = {
        GUID: event.payload.guid,
        MEMBER_GUID: event.payload.member_guid,
        NAME: event.payload.name,
        ACCOUNT_SUBTYPE: event.payload.account_subtype,
        BALANCE: event.payload.balance,
        AVAILABLE_BALANCE: event.payload.balance,
        IS_CLOSED: event.payload.is_closed,
        IS_HIDDEN: event.payload.is_hidden,
        IS_MANUAL: event.payload.is_manual,
        PROPERTY_TYPE: event.payload.property_type,
        METADATA: event.payload.metadata,
      } as MxAccount;
      queryClient.setQueryData(QUERY_KEY, [
        ...(previousAccounts || []),
        newAccount,
      ]);
    }
  }
};

export { updateAccountBalance };
